import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DivineLight from "./components/DivineLight";
import SacredBook from "./components/SacredBook";
import TwelveStepsScroll from "./components/TwelveStepsScroll";
import LightTunnel from "./components/LightTunnel";
import { usePhaseAudio } from "./usePhaseAudio";
import type { PhaseType } from "./usePhaseAudio";

type Stage =
  | "darkness"
  | "light_descend"
  | "book_appear"
  | "book_open"
  | "signing"
  | "portal";

const PHASE_BG: Record<PhaseType, string> = {
  idle:        "#04030a",
  "PHASE I":   "#0a0203",
  "PHASE II":  "#080400",
  "PHASE III": "#05040a",
};

const PHASE_BORDER: Record<PhaseType, string> = {
  idle:        "rgba(201,168,76,0.15)",
  "PHASE I":   "rgba(180,50,30,0.25)",
  "PHASE II":  "rgba(200,120,30,0.25)",
  "PHASE III": "rgba(255,210,80,0.3)",
};

const PHASE_BOOK_BG: Record<PhaseType, string> = {
  idle:        "rgba(8,5,2,0.93)",
  "PHASE I":   "rgba(10,2,2,0.95)",
  "PHASE II":  "rgba(10,5,0,0.95)",
  "PHASE III": "rgba(8,6,2,0.93)",
};

// ── 天界のエントリー背景 Canvas ──
function HeavenEntryCanvas({ intensity }: { intensity: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const intRef = useRef(intensity);
  useEffect(() => { intRef.current = intensity; }, [intensity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // 天界の星
    interface Star { x: number; y: number; r: number; opacity: number; phase: number; }
    const stars: Star[] = Array.from({ length: 160 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080) * 0.65,
      r: 0.3 + Math.random() * 1.2,
      opacity: 0.15 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
    }));

    // 天界からの光の雨
    interface LightRain { x: number; y: number; vy: number; len: number; opacity: number; }
    const rains: LightRain[] = Array.from({ length: 25 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080) * 0.5,
      vy: 0.6 + Math.random() * 1.2,
      len: 20 + Math.random() * 60,
      opacity: 0.04 + Math.random() * 0.1,
    }));

    // 巨大な光の柱（天界から降下）
    interface BigPillar { x: number; width: number; opacity: number; phase: number; }
    const bigPillars: BigPillar[] = Array.from({ length: 5 }, () => ({
      x: (canvas.width || 1920) * (0.05 + Math.random() * 0.9),
      width: 60 + Math.random() * 140,
      opacity: 0.02 + Math.random() * 0.035,
      phase: Math.random() * Math.PI * 2,
    }));

    let t = 0;

    function draw() {
      t++;
      const w = canvas.width;
      const h = canvas.height;
      const iv = intRef.current;

      ctx.clearRect(0, 0, w, h);

      // 天界グラデ（上から下）
      const skyGrd = ctx.createLinearGradient(0, 0, 0, h);
      skyGrd.addColorStop(0, `rgba(6, 4, 18, ${0.85 + iv * 0.1})`);
      skyGrd.addColorStop(0.4, `rgba(8, 5, 20, 0.7)`);
      skyGrd.addColorStop(1, "rgba(4, 3, 10, 0)");
      ctx.fillStyle = skyGrd;
      ctx.fillRect(0, 0, w, h);

      // 大きな光柱
      bigPillars.forEach((p) => {
        p.phase += 0.005;
        const pulse = Math.sin(p.phase) * 0.4 + 0.6;
        const alpha = p.opacity * (0.3 + iv * 0.7) * pulse;
        const pg = ctx.createLinearGradient(0, 0, 0, h * 0.85);
        pg.addColorStop(0, `rgba(255, 235, 150, 0)`);
        pg.addColorStop(0.15, `rgba(255, 220, 120, ${alpha})`);
        pg.addColorStop(0.6, `rgba(255, 200, 80, ${alpha * 0.6})`);
        pg.addColorStop(1, `rgba(255, 180, 40, 0)`);
        ctx.fillStyle = pg;
        ctx.fillRect(p.x - p.width / 2, 0, p.width, h * 0.85);
      });

      // 星
      stars.forEach((s) => {
        s.phase += 0.012;
        const a = s.opacity * (0.5 + Math.sin(s.phase) * 0.5) * (0.3 + iv * 0.7);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 245, 210, ${a})`;
        ctx.fill();
        if (s.opacity > 0.4 && a > 0.15) {
          const cl = s.r * 4;
          ctx.beginPath();
          ctx.moveTo(s.x - cl, s.y); ctx.lineTo(s.x + cl, s.y);
          ctx.strokeStyle = `rgba(255, 245, 210, ${a * 0.35})`;
          ctx.lineWidth = 0.4;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(s.x, s.y - cl); ctx.lineTo(s.x, s.y + cl);
          ctx.stroke();
        }
      });

      // 光の雨
      rains.forEach((r) => {
        r.y += r.vy;
        if (r.y > h * 0.55) { r.y = -r.len; r.x = Math.random() * w; }
        const rg = ctx.createLinearGradient(r.x, r.y, r.x, r.y + r.len);
        rg.addColorStop(0, "rgba(255,230,140,0)");
        rg.addColorStop(0.5, `rgba(255,220,120,${r.opacity * iv})`);
        rg.addColorStop(1, "rgba(255,210,100,0)");
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x, r.y + r.len);
        ctx.strokeStyle = rg;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // 中央の収束グロー（書物が開く前の神聖な光）
      const convergePulse = Math.sin(t * 0.012) * 0.3 + 0.7;
      const cxv = w / 2;
      const cyv = h * 0.42;
      const convR = 120 * iv * convergePulse;
      if (convR > 5) {
        const convGrd = ctx.createRadialGradient(cxv, cyv, 0, cxv, cyv, convR);
        convGrd.addColorStop(0, `rgba(255, 235, 160, ${0.12 * iv * convergePulse})`);
        convGrd.addColorStop(0.4, `rgba(255, 215, 100, ${0.06 * iv})`);
        convGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(cxv, cyv, convR, 0, Math.PI * 2);
        ctx.fillStyle = convGrd;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
}

export default function RevelationPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("darkness");
  const [lightIntensity, setLightIntensity] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<PhaseType>("PHASE I");
  const audioStartedRef = useRef(false);
  const sequenceStartedRef = useRef(false);

  const { start: startAudio, transitionToPhase, playPhaseChime, playSigningChord, stop: stopAudio } = usePhaseAudio();

  useEffect(() => {
    const fromAbyss = sessionStorage.getItem("from_abyss") === "true";
    if (!fromAbyss) {
      sessionStorage.setItem("from_abyss", "true");
    }
  }, []);

  useEffect(() => {
    if (sequenceStartedRef.current) return;
    sequenceStartedRef.current = true;

    const t1 = setTimeout(() => {
      setStage("light_descend");
      let v = 0;
      const inc = setInterval(() => {
        v += 0.012;
        setLightIntensity(Math.min(v, 0.75));
        if (v >= 0.75) clearInterval(inc);
      }, 60);
    }, 500);

    const t2 = setTimeout(() => setTextVisible(true), 1200);
    const t3 = setTimeout(() => setStage("book_appear"), 2600);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFirstInteraction = useCallback(() => {
    if (audioStartedRef.current) return;
    audioStartedRef.current = true;
    startAudio();
  }, [startAudio]);

  const handleBookOpen = useCallback(() => {
    handleFirstInteraction();
    let v = lightIntensity;
    const inc = setInterval(() => {
      v += 0.02;
      setLightIntensity(Math.min(v, 1));
      if (v >= 1) clearInterval(inc);
    }, 50);
    setTimeout(() => {
      setStage("book_open");
      setCurrentPhase("PHASE I");
      transitionToPhase("PHASE I");
      playPhaseChime("PHASE I");
    }, 300);
  }, [lightIntensity, handleFirstInteraction, transitionToPhase, playPhaseChime]);

  const handlePhaseChange = useCallback((phase: PhaseType) => {
    setCurrentPhase(phase);
    transitionToPhase(phase);
    playPhaseChime(phase);
  }, [transitionToPhase, playPhaseChime]);

  const handleStepsComplete = useCallback(() => {
    playSigningChord();
    setStage("signing");
  }, [playSigningChord]);

  const handleTunnelNavigate = useCallback(() => {
    stopAudio(0.8);
    sessionStorage.removeItem("from_abyss");
    sessionStorage.setItem("vault_unlocked", "true");
    navigate("/vault");
  }, [navigate, stopAudio]);

  const handleTunnelComplete = useCallback(() => {}, []);

  const isBookOpen = stage === "book_open" || stage === "signing";
  const isTunnelActive = stage === "signing";
  const bgColor = isBookOpen ? PHASE_BG[currentPhase] : "#04030a";
  const bookBorder = PHASE_BORDER[currentPhase];
  const bookBg = PHASE_BOOK_BG[currentPhase];

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center"
      style={{ background: bgColor, transition: "background 3s ease" }}
      onClick={handleFirstInteraction}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');
        @keyframes bookOpenAnim {
          from { clip-path: inset(50% 50% 50% 50%); opacity: 0; }
          to   { clip-path: inset(0% 0% 0% 0%); opacity: 1; }
        }
        @keyframes entryTextFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-4px); }
        }
        @keyframes divineLine {
          0%, 100% { opacity: 0.4; transform: scaleX(1); }
          50%       { opacity: 0.8; transform: scaleX(1.06); }
        }
        @keyframes haloSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── 書物が開く前：天界エントリーCanvas ── */}
      {!isBookOpen && (
        <HeavenEntryCanvas intensity={lightIntensity} />
      )}

      {/* ── 書物が開いた後：DivineLight ── */}
      {isBookOpen && (
        <DivineLight intensity={lightIntensity} phase={currentPhase} />
      )}

      {/* 背景テクスチャ（書物が開く前） */}
      {!isBookOpen && (
        <>
          <div
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              backgroundImage: `url(https://public.readdy.ai/ai/img_res/866518a73f6d3f8993d515ed18257669.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              opacity: lightIntensity * 0.28,
              transition: "opacity 2s ease",
            }}
          />
          <div
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse 80% 70% at 50% 20%, rgba(20,12,4,0.6) 0%, rgba(4,3,10,0.92) 65%)",
            }}
          />
        </>
      )}

      {/* スキャンライン */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.025) 3px, rgba(0,0,0,0.025) 6px)",
        }}
      />

      {/* ════════════════════════════════════════
          書物が開く前：エントリーUI
      ════════════════════════════════════════ */}
      {!isBookOpen && (
        <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-2xl mx-auto w-full">

          {/* 天界の光柱（上部） */}
          <div
            className="mb-10"
            style={{
              opacity: stage !== "darkness" ? 1 : 0,
              transition: "opacity 2.5s ease",
            }}
          >
            <div
              className="mx-auto"
              style={{
                width: "1px",
                height: "72px",
                background: "linear-gradient(to bottom, rgba(255,240,160,0.5), rgba(255,210,80,0.15), transparent)",
              }}
            />
            {/* 天頂の光輪 */}
            <div
              className="mx-auto mt-2"
              style={{
                width: "30px",
                height: "8px",
                borderRadius: "50%",
                border: "1px solid rgba(201,168,76,0.3)",
                boxShadow: "0 0 16px rgba(255,200,60,0.2)",
                animation: "haloSpin 8s linear infinite",
              }}
            />
          </div>

          {/* ラベル */}
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "rgba(255,220,120,0.45)",
              fontSize: "8.5px",
              letterSpacing: "0.75em",
              marginBottom: "18px",
              opacity: textVisible ? 1 : 0,
              transition: "opacity 2.5s ease",
              textTransform: "uppercase",
            }}
          >
            Divine Revelation · 神の啓示
          </div>

          {/* メインタイトル */}
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "rgba(255,245,210,0.85)",
              fontSize: "clamp(2.2rem, 5vw, 4rem)",
              fontWeight: 300,
              letterSpacing: "0.08em",
              lineHeight: 1.25,
              marginBottom: "14px",
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? "translateY(0)" : "translateY(18px)",
              transition: "opacity 2.2s ease 0.2s, transform 2.2s ease 0.2s",
              textShadow: "0 0 80px rgba(255,210,80,0.2), 0 0 160px rgba(255,180,40,0.08)",
              animation: textVisible ? "entryTextFloat 6s ease-in-out infinite" : "none",
            }}
          >
            天界から
            <br />
            <span
              style={{
                color: "rgba(201,168,76,0.75)",
                fontSize: "0.68em",
                letterSpacing: "0.22em",
                display: "block",
                marginTop: "4px",
              }}
            >
              啓示が降りてくる
            </span>
          </h1>

          {/* 装飾ライン */}
          <div
            className="mx-auto mb-8"
            style={{
              width: "50px",
              height: "1px",
              background: "rgba(201,168,76,0.35)",
              opacity: textVisible ? 1 : 0,
              transition: "opacity 2s ease 0.7s",
              animation: textVisible ? "divineLine 4s ease-in-out infinite" : "none",
            }}
          />

          {/* サブテキスト */}
          <p
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "rgba(255,255,255,0.18)",
              fontSize: "12px",
              letterSpacing: "0.28em",
              lineHeight: 2.2,
              marginBottom: "64px",
              fontStyle: "italic",
              opacity: textVisible ? 1 : 0,
              transition: "opacity 2s ease 1s",
            }}
          >
            準備が整ったとき、書物は開かれる。
            <br />
            <span style={{ color: "rgba(255,220,120,0.2)", fontSize: "0.9em", letterSpacing: "0.2em" }}>
              神々はすでに、あなたを知っている。
            </span>
          </p>

          {/* 書物 */}
          <SacredBook visible={stage === "book_appear"} onOpen={handleBookOpen} />

          {/* 下部の光柱 */}
          <div
            className="mt-10"
            style={{
              opacity: stage === "book_appear" ? 1 : 0,
              transition: "opacity 2s ease 1.2s",
            }}
          >
            <div
              className="mx-auto"
              style={{
                width: "1px",
                height: "44px",
                background: "linear-gradient(to bottom, rgba(255,200,60,0.12), transparent)",
              }}
            />
          </div>

          {/* 四隅の天界装飾 */}
          {textVisible && (
            <>
              {[
                { top: "0px", left: "0px" },
                { top: "0px", right: "0px" },
                { bottom: "0px", left: "0px" },
                { bottom: "0px", right: "0px" },
              ].map((pos, i) => (
                <div
                  key={i}
                  className="absolute w-6 h-6"
                  style={{
                    ...pos,
                    borderTop: i < 2 ? "1px solid rgba(201,168,76,0.12)" : "none",
                    borderBottom: i >= 2 ? "1px solid rgba(201,168,76,0.12)" : "none",
                    borderLeft: i % 2 === 0 ? "1px solid rgba(201,168,76,0.12)" : "none",
                    borderRight: i % 2 === 1 ? "1px solid rgba(201,168,76,0.12)" : "none",
                    opacity: textVisible ? 1 : 0,
                    transition: `opacity 2s ease ${1.5 + i * 0.2}s`,
                  }}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════
          書物が開いた後：12ステップ
      ════════════════════════════════════════ */}
      {isBookOpen && (
        <div
          className="relative z-10 w-full"
          style={{
            position: "fixed",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: "680px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            padding: "28px 20px 20px",
          }}
        >
          {/* 書物の外コンテナ */}
          <div
            style={{
              flex: "1 1 0",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${bookBorder}`,
              background: bookBg,
              padding: "26px 22px 18px",
              position: "relative",
              animation: "bookOpenAnim 0.85s ease forwards",
              transition: "border-color 3s ease, background 3s ease",
            }}
          >
            {/* 内枠 */}
            <div
              className="absolute pointer-events-none"
              style={{
                inset: "10px",
                border: `1px solid ${bookBorder}`,
                opacity: 0.3,
                transition: "border-color 3s ease",
              }}
            />

            {/* 背表紙感（左縦線） */}
            <div
              className="absolute left-0 top-0 bottom-0 pointer-events-none"
              style={{
                width: "8px",
                background: "linear-gradient(to right, rgba(0,0,0,0.7), transparent)",
                borderRight: `1px solid ${bookBorder}`,
                transition: "border-color 3s ease",
              }}
            />

            {/* 四隅の金の装飾 */}
            {[
              { top: "10px", left: "10px" },
              { top: "10px", right: "10px" },
              { bottom: "10px", left: "10px" },
              { bottom: "10px", right: "10px" },
            ].map((pos, i) => (
              <div
                key={i}
                className="absolute w-4 h-4 pointer-events-none"
                style={{
                  ...pos,
                  borderTop: i < 2 ? `1px solid ${bookBorder}` : "none",
                  borderBottom: i >= 2 ? `1px solid ${bookBorder}` : "none",
                  borderLeft: i % 2 === 0 ? `1px solid ${bookBorder}` : "none",
                  borderRight: i % 2 === 1 ? `1px solid ${bookBorder}` : "none",
                  opacity: 0.5,
                  transition: "border-color 3s ease",
                }}
              />
            ))}

            {/* フェーズインジケーター（右上） */}
            <div
              className="absolute top-3 right-5 pointer-events-none"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: PHASE_BORDER[currentPhase],
                fontSize: "7.5px",
                letterSpacing: "0.5em",
                transition: "color 2s ease",
              }}
            >
              {currentPhase}
            </div>

            {/* 左側：天使のシンボルライン */}
            <div
              className="absolute pointer-events-none"
              style={{
                left: "22px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
                opacity: 0.2,
              }}
            >
              <div style={{ width: "1px", height: "24px", background: PHASE_BORDER[currentPhase] }} />
              <div
                style={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  border: `1px solid ${PHASE_BORDER[currentPhase]}`,
                }}
              />
              <div style={{ width: "1px", height: "24px", background: PHASE_BORDER[currentPhase] }} />
            </div>

            <TwelveStepsScroll
              onComplete={handleStepsComplete}
              onPhaseChange={handlePhaseChange}
              currentPhase={currentPhase}
            />
          </div>
        </div>
      )}

      {/* 光のトンネル遷移 */}
      <LightTunnel
        active={isTunnelActive}
        onNavigate={handleTunnelNavigate}
        onComplete={handleTunnelComplete}
      />
    </div>
  );
}
